import { AfterViewInit, Component, Inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ValidationErrors } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { ToastrService } from 'ngx-toastr';
import Inputmask from 'inputmask';

import { BaseFormComponent } from '../../base-form/base-form.component';
import { ICustoEquipePlanejamentoModel } from 'src/app/models/custo-equipe-planejamento.model';
import { AgentePublicoService } from 'src/app/services/agente-publico.service';
import { FiltroModel } from 'src/app/models/filtro.model';
import { CustoEquipePlanejamentoService } from 'src/app/services/custo-equipe-planejamento.service';

@Component({
  selector: 'app-custos-equipe-form-modal',
  templateUrl: './custos-equipe-form-modal.component.html',
  styleUrls: ['./custos-equipe-form-modal.component.scss']
})
export class CustosEquipeFormModalComponent extends BaseFormComponent implements OnInit, AfterViewInit {
  private registrosExistentes: ICustoEquipePlanejamentoModel[] = [];
  form: FormGroup;
  agentes: { id: number; nome: string }[] = [];

  constructor(
    route: ActivatedRoute,
    toastr: ToastrService,
    router: Router,
    localeService: BsLocaleService,
    public matDialog: MatDialog,
    private dialogRef: MatDialogRef<CustosEquipeFormModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private agentePublicoService: AgentePublicoService,
    private custoEquipeService: CustoEquipePlanejamentoService,
  ) {
    super(route, toastr, router, localeService, matDialog);
    this.registrosExistentes = data?.registrosExistentes || [];
  }

  get minDateFim(): Date | undefined {
    const inicio = this.form?.get('dataInicio')?.value;
    return inicio ? new Date(inicio) : undefined;
  }

  get maxDateInicio(): Date | undefined {
    const fim = this.form?.get('dataFim')?.value;
    return fim ? new Date(fim) : undefined;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      id: [this.data?.id || 0],
      planejamentoId: [this.data?.planejamentoId, Validators.required],
      secaoId: [this.data?.secaoId || 0, Validators.required],
      agentePublicoId: [this.data?.agentePublicoId, Validators.required],
      dataInicio: [this.data?.dataInicio || null, Validators.required],
      dataFim: [this.data?.dataFim || null, Validators.required],
      diasCorridos: [{ value: this.data?.diasCorridos || 0, disabled: true }],
      salario: [this.data?.salario || null],
      custoFinal: [{ value: this.data?.custoFinal || 0, disabled: true }]
    }, {
      validators: this.validarDatas.bind(this)
    });

    this.titulo = 'Equipe';

    this.carregarAgentes();

    this.form.get('dataInicio')?.valueChanges.subscribe(() => {
      this.validarEAtualizarDataFim();
      this.atualizarCalculos();
    });

    this.form.get('dataFim')?.valueChanges.subscribe(() => {
      this.validarEAtualizarDataFim();
      this.atualizarCalculos();
    });

    this.form.get('salario')?.valueChanges.subscribe(() => {
      this.atualizarCalculos();
    });
  }

  ngAfterViewInit(): void {
    Inputmask("99/99/9999").mask(document.querySelectorAll('input[data-mask="date"]'));
  }

  carregarAgentes(): void {
    const filtro = new FiltroModel();
    filtro.ativo = true;
    this.agentePublicoService
      .obterAtivos()
      .then((res) => {
        const resultado = res?.dados || [];
        this.agentes = resultado.map(item => ({
          id: item.idAgentePublico,
          nome: item.pessoa?.nome ?? 'Não informado'
        }));
      })
      .catch((err) => {
        this.exibirMensagemAlerta(err);
      });
  }

  atualizarCalculos(): void {
    const inicio: Date = this.form.get('dataInicio')?.value;
    const fim: Date = this.form.get('dataFim')?.value;
    const salario = this.parseSalarioParaFloat(this.form.get('salario')?.value);

    if (inicio instanceof Date && fim instanceof Date) {
      if (fim < inicio) {
        this.form.get('diasCorridos')?.setValue(0);
        this.form.get('custoFinal')?.setValue(0);
        return;
      }

      const dias = Math.floor((fim.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      this.form.get('diasCorridos')?.setValue(dias);

      if (!salario || isNaN(salario)) {
        this.form.get('custoFinal')?.setValue(null);
        return;
      }

      const custoFinal = +(salario / 30 * dias).toFixed(2);
      this.form.get('custoFinal')?.setValue(custoFinal > 0 ? custoFinal : 0);
    } else {
      this.form.get('diasCorridos')?.setValue(0);
      this.form.get('custoFinal')?.setValue(null);
    }
  }

  private validarEAtualizarDataFim(): void {
    const inicio = this.form.get('dataInicio')?.value;
    const fim = this.form.get('dataFim')?.value;

    if (inicio && fim && fim < inicio) {
      this.form.get('dataFim')?.setValue(inicio);
    }
  }

  validarDatas(form: FormGroup): ValidationErrors | null {
    const inicio = form.get('dataInicio');
    const fim = form.get('dataFim');

    if (inicio && fim && inicio.value && fim.value) {
      if (fim.value < inicio.value) {
        const existingErrors = inicio.errors || {};
        inicio.setErrors({ ...existingErrors, dataMaiorQueFim: true });
      } else {
        const errors = inicio.errors;
        if (errors) {
          delete errors.dataMaiorQueFim;
          inicio.setErrors(Object.keys(errors).length > 0 ? errors : null);
        }
      }
    }

    return null;
  }

  formatarValorVisual(valor: number | null | undefined): string {
    if (valor === null || valor === undefined || isNaN(valor) || valor === 0) {
      return '';
    }

    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    });
  }


  onInputSalario(event: any): void {
    const raw = event.target.value.replace(/\D/g, '');

    if (!raw || raw === 0) {
      event.target.value = '';
      this.form.get('salario')?.setValue(null, { emitEvent: false });
      this.atualizarCalculos();
      return;
    }

    const floatValue = (parseFloat(raw) / 100).toFixed(2);
    const valorFormatado = parseFloat(floatValue).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    });

    event.target.value = valorFormatado;
    this.form.get('salario')?.setValue(valorFormatado, { emitEvent: false });

    this.atualizarCalculos();
  }

  private parseSalarioParaFloat(valor: string | number): number {
    if (typeof valor === 'number') return valor;

    let str = (valor || '').toString().trim();
    if (str.startsWith('R$')) {
      str = str.replace(/\s/g, '').replace('R$', '').replace(/\./g, '').replace(',', '.');
    }
    return str ? parseFloat(str) || null : null;
  }

  salvar(fechar: boolean): void {
    this.submit = true;

    if (this.form.invalid) return;

    if (this.periodoSobreposto()) {
      this.toastr.warning('Período já adicionado em outro item.');
      return;
    }

    const model = this.form.getRawValue();
    model.salario = this.parseSalarioParaFloat(model.salario);

    const request$ = model.id && model.id > 0
      ? this.custoEquipeService.atualizar(model)
      : this.custoEquipeService.inserir(model);

    request$.subscribe({
      next: (res) => {
        if (res.sucesso) {
           this.form.get('id')?.setValue(res.dados.id);
          this.toastr.success('Registro salvo com sucesso!');
          if (fechar) {
            this.dialogRef.close(true);
          }
        } else {
          this.exibirMensagemErro(res.mensagem?.descricao || 'Erro ao salvar');
        }
      },
      error: (err) => this.exibirMensagemErro(err)
    });
  }

  private periodoSobreposto(): boolean {
    const idAtual = this.form.get('id')?.value;
    const agenteId = this.form.get('agentePublicoId')?.value;
    const dataInicio = this.form.get('dataInicio')?.value;
    const dataFim = this.form.get('dataFim')?.value;

    if (!agenteId || !dataInicio || !dataFim) return false;

    return this.registrosExistentes.some(item => {
      if (item.id === idAtual || item.agentePublicoId !== agenteId) return false;

      const inicioExistente = new Date(item.dataInicio);
      const fimExistente = new Date(item.dataFim);

      return (
        (dataInicio <= fimExistente && dataFim >= inicioExistente)
      );
    });
  }

  fechar(): void {
    this.dialogRef.close(true);
  }
}
